import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GoogleAuthProvider, reauthenticateWithPopup, signOut } from "firebase/auth";

import { auth } from "config/firebase-config";
import { useUserAuth } from "context/UserContext";
import { deleteAccount, getDeleteAccountCheck } from "services/accountService";
import { formatPlanPrice, localeFor } from "components/Studio/studioText";

/* "Delete account", at the bottom of Account settings → Security.

   In the order the person meets it:
     1. one button, which first asks the backend what stands in the way
     2. anything blocking (a plan still billing, credits...) is listed with
        what to do about it, and there is NO delete button
     3. otherwise a confirmation: what goes (Studio apps included, with their
        number typed back), what stays, an unticked "I understand" box, and
        proof it is really them (password or Google)
     4. the deletion itself, then sign-out here and /account-deleted

   The backend re-checks every blocker and the recent sign-in on its own; the
   checks here are so people get a clear answer, not the security. */

// Prompt Lab keeps per-lesson chat drafts in this browser under
// promptLabDraft:<uid>:<lesson> (see PromptLabHub/chatDraft.js). The server
// copy is gone after deletion, so leaving these behind would keep the person's
// chats on a possibly shared computer.
const DRAFT_PREFIX = "promptLabDraft:";

// Credits are whole US cents, shown with the currency code ("USD 25.00"): a
// bare "$" reads as pesos to much of the Spanish-speaking audience.
const formatCredits = (credits, lang) =>
  formatPlanPrice({ amount: Number(credits) || 0, currency: "usd" }, localeFor(lang));

const BLOCKER_KEYS = {
  active_subscription: "blockerActiveSubscription",
  open_invoice: "blockerOpenInvoice",
  unused_credits: "blockerUnusedCredits",
  billing_unavailable: "blockerBillingUnavailable",
};

// Blockers only a person at Codenec can clear, so they come with a way to
// reach us. An unknown code (a newer backend) is treated the same way: telling
// someone to contact us is never wrong, leaving them stuck with no next step is.
const NEEDS_CONTACT = new Set(["open_invoice", "unused_credits"]);

// The delete call's 409 when the typed number of Studio apps is missing or no
// longer matches: { code, count } with the current count.
const APPS_UNCONFIRMED = "studio_apps_unconfirmed";

// What Firebase says when the account it is asked about no longer exists.
// _reauthenticate turns user-not-found into user-mismatch, and projects with
// email-enumeration protection answer invalid-credential instead; a refresh
// for a deleted user fails with user-token-expired. On their own each also
// has an ordinary meaning (a typo, an old session), so they only mean "maybe
// already deleted" right after a delete request whose answer never arrived.
const MAYBE_DELETED_CODES = new Set([
  "auth/user-not-found",
  "auth/user-mismatch",
  "auth/user-token-expired",
  "auth/invalid-credential",
]);

// How this person proves it is them. Password first when an account has both,
// because typing it is quicker than a popup. "none" (no provider we can
// re-check here) still works: the backend refuses a stale sign-in with
// auth/requires-recent-login and the page says so.
const reauthMethodFor = (firebaseUser) => {
  const providers = (firebaseUser?.providerData || []).map((p) => p?.providerId);
  if (providers.includes("password")) return "password";
  if (providers.includes("google.com")) return "google";
  return "none";
};

const clearPromptLabDrafts = (uid) => {
  if (!uid) return;
  try {
    const store = window.localStorage;
    const prefix = `${DRAFT_PREFIX}${uid}:`;
    const doomed = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (typeof key === "string" && key.startsWith(prefix)) doomed.push(key);
    }
    // Collected first and removed after: removing while walking the index
    // shifts the keys under the loop and skips every other one.
    doomed.forEach((key) => store.removeItem(key));
  } catch (error) {
    // Storage disabled (private mode). Nothing was saved there, then.
  }
};

// Firebase errors carry `code` themselves; the backend's come back as
// response.data.code. Axios sets its own `code` ("ERR_BAD_REQUEST") on every
// HTTP error, so the backend's has to be read first or it is never seen.
const errorKeyFor = (error, method) => {
  const code = error?.response?.data?.code || error?.code;
  switch (code) {
    // Newer Firebase projects collapse a wrong password into
    // invalid-credential so the response cannot confirm which accounts exist.
    case "auth/wrong-password":
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
      return "errorWrongPassword";
    case "auth/requires-recent-login":
      return "errorReauthAgain";
    // Only the Google popup lets someone pick a different account. On the
    // password path Firebase reports an account that is gone this way, and
    // telling a password user to "choose the same Google account" makes no sense.
    case "auth/user-mismatch":
      return method === "google" ? "errorGoogleMismatch" : "errorSession";
    case "auth/popup-blocked":
      return "errorPopupBlocked";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "errorPopupClosed";
    case "auth/too-many-requests":
      return "errorTooManyAttempts";
    case "auth/network-request-failed":
      return "errorNetwork";
    case "auth/no-current-user":
    case "auth/user-not-found":
    case "auth/user-token-expired":
      return "errorSession";
    default:
      break;
  }
  const status = error?.response?.status;
  if (status === 429) return "errorRateLimited";
  if (status === 401) return "errorSession";
  if (error?.request && !error?.response) return "errorNetwork";
  return "errorGeneric";
};

// The delete request left this page but we cannot tell how it ended: the
// answer was lost on the way back, or the server failed partway. Either way
// the deletion may have finished on the server.
const outcomeUnknown = (error) =>
  Boolean(error?.request && !error?.response) || Number(error?.response?.status) >= 500;

export default function DeleteAccountSection({ onGoToBilling }) {
  const { t, i18n } = useTranslation();
  const { user, setUser, reauthenticate } = useUserAuth();
  const navigate = useNavigate();

  // idle → (blocked | confirm). `checking` is separate so the panel on screen
  // stays put while "Check again" runs.
  const [step, setStep] = useState("idle");
  const [checking, setChecking] = useState(false);
  const [blockers, setBlockers] = useState([]);
  const [understood, setUnderstood] = useState(false);
  const [password, setPassword] = useState("");
  const [googleConfirmed, setGoogleConfirmed] = useState(false);
  const [working, setWorking] = useState(null); // null | "google" | "delete"
  const [error, setError] = useState("");
  // The check's summary: studioApps, studioMessagesLeft, studioTrial...
  const [summary, setSummary] = useState({});
  // The number of Studio apps, typed back by the person.
  const [appsTyped, setAppsTyped] = useState("");
  const [appsError, setAppsError] = useState(false);
  // Where focus should go once the next render is on screen. A fresh object
  // per request, so asking for the same element twice still moves focus.
  const [focusRequest, setFocusRequest] = useState(null);

  const startRef = useRef(null);
  const checkAgainRef = useRef(null);
  const blockedTitleRef = useRef(null);
  const confirmTitleRef = useRef(null);
  const checkboxRef = useRef(null);
  const passwordRef = useRef(null);
  const googleButtonRef = useRef(null);
  const deleteButtonRef = useRef(null);
  const appsRef = useRef(null);
  // True after a delete request whose outcome we never learned (see
  // outcomeUnknown). Cleared as soon as anything proves the account still
  // exists (a successful re-auth or check, or a real answer from the server).
  const mayBeDeletedRef = useRef(false);

  // Each panel appears in place of the button that opened it, so focus moves
  // to its heading (not to an answer: nothing is pre-selected) and keyboard
  // and screen-reader users land in it.
  useEffect(() => {
    if (step === "blocked") blockedTitleRef.current?.focus();
    else if (step === "confirm") confirmTitleRef.current?.focus();
  }, [step]);

  // Runs after the render that the request was made alongside, so the target
  // is mounted and no longer disabled by then. Focusing straight from the async
  // handlers would hit the element before React has re-enabled it.
  useEffect(() => {
    focusRequest?.ref.current?.focus();
  }, [focusRequest]);

  // Guests (anonymous accounts) have no profile, sign-in or billing of their
  // own to delete, and the backend refuses them on this route.
  if (!user || user.isAnonymous) return null;

  const currentUser = auth.currentUser;
  const method = reauthMethodFor(currentUser);
  const proved = method === "password" ? password.length > 0 : method === "google" ? googleConfirmed : true;
  // Studio apps are deleted with the account, so their number is typed back.
  const studioApps = Math.max(0, Number(summary?.studioApps) || 0);
  const appsConfirmed = studioApps === 0 || (appsTyped.trim() !== "" && Number(appsTyped) === studioApps);
  // Plan and free AI messages left over are lost with the account.
  const forfeitsMessages = Number(summary?.studioMessagesLeft) > 0 || summary?.studioTrial === true;
  // Ready apart from a request in flight. The button's `disabled` follows
  // this, not `working`, so it never switches off under the person's focus.
  const ready = understood && proved && appsConfirmed;
  const canDelete = ready && working === null;

  // The backend deletes contact-form messages only for an address the account
  // has PROVEN it owns (verified), because anyone can type someone else's
  // address into an account. The list below has to promise exactly that, the
  // same rule as the server's emailProven. A Google sign-in alone does not
  // count: the account's address can have been changed since.
  const contactEmail = currentUser?.email || user.email || "";
  const contactProven = Boolean(contactEmail) && currentUser?.emailVerified === true;

  const focusLater = (ref) => setFocusRequest({ ref });

  // Firebase answers that, right after a delete with no clear answer, most
  // likely mean the account is already gone rather than what they normally say.
  const looksAlreadyDeleted = (err) =>
    mayBeDeletedRef.current && !err?.response && MAYBE_DELETED_CODES.has(err?.code);

  const resetConfirm = () => {
    setUnderstood(false);
    setPassword("");
    setGoogleConfirmed(false);
    setAppsTyped("");
    setAppsError(false);
  };

  const showBlockers = (found) => {
    resetConfirm();
    // A 409 without a list still means "blocked": show the contact-us line
    // rather than an empty box.
    setBlockers(Array.isArray(found) && found.length ? found : [{ code: "unknown" }]);
    setStep("blocked");
  };

  const runCheck = async () => {
    // aria-disabled, not disabled, while checking: a disabled button drops the
    // focus it holds to <body>, so the guard lives here instead.
    if (checking) return;
    const from = step;
    setError("");
    setChecking(true);
    try {
      const result = await getDeleteAccountCheck();
      mayBeDeletedRef.current = false;
      const found = Array.isArray(result?.blockers) ? result.blockers : [];
      setSummary(result?.summary && typeof result.summary === "object" ? result.summary : {});
      if (found.length) {
        showBlockers(found);
        // "Check again" with blockers still there keeps the same step, so the
        // step effect does not fire: bring focus back to the panel's heading
        // so the (possibly new) list is read from the top.
        if (from === "blocked") focusLater(blockedTitleRef);
      } else {
        resetConfirm();
        setBlockers([]);
        setStep("confirm");
      }
    } catch (err) {
      let key = "checkFailed";
      if (err?.response?.status === 429) key = "errorRateLimited";
      else if (looksAlreadyDeleted(err)) key = "errorMaybeDeleted";
      setError(t(`deleteAccount.${key}`));
      focusLater(from === "blocked" ? checkAgainRef : startRef);
    } finally {
      setChecking(false);
    }
  };

  const cancel = () => {
    resetConfirm();
    setBlockers([]);
    setError("");
    setStep("idle");
    // The start button mounts in this same render; hand focus back to it.
    focusLater(startRef);
  };

  const confirmWithGoogle = async () => {
    if (working !== null) return;
    setError("");
    setWorking("google");
    try {
      await reauthenticateWithPopup(auth.currentUser, new GoogleAuthProvider());
      mayBeDeletedRef.current = false;
      setGoogleConfirmed(true);
      // The Google button goes away now, so focus moves on to the next thing
      // to do: tick the box, or press Delete if it is already ticked.
      focusLater(understood ? deleteButtonRef : checkboxRef);
    } catch (err) {
      setGoogleConfirmed(false);
      const key = looksAlreadyDeleted(err) ? "errorMaybeDeleted" : errorKeyFor(err, "google");
      setError(t(`deleteAccount.${key}`));
      focusLater(googleButtonRef);
    } finally {
      setWorking(null);
    }
  };

  const submitDelete = async (event) => {
    event.preventDefault();
    if (!canDelete) return;

    setError("");
    setAppsError(false);
    setWorking("delete");
    // Read before anything signs out: auth.currentUser is null afterwards.
    const uid = user.uid || auth.currentUser?.uid;
    const locale = String(i18n?.language || "").toLowerCase().startsWith("es") ? "es" : "en";

    let result;
    try {
      // Right before the call, so the fresh token accountService fetches
      // carries a sign-in time the backend accepts (under 10 minutes).
      if (method === "password") {
        await reauthenticate(password);
        mayBeDeletedRef.current = false;
      }
      result = await deleteAccount(
        studioApps > 0 ? { locale, studioAppsConfirmed: Number(appsTyped) } : { locale }
      );
    } catch (err) {
      setWorking(null);
      // Something changed since the check (a plan started, credits landed).
      if (err?.response?.status === 409) {
        mayBeDeletedRef.current = false;
        const found = Array.isArray(err.response.data?.blockers) ? err.response.data.blockers : [];
        const unconfirmed = found.find((b) => b?.code === APPS_UNCONFIRMED);
        const others = found.filter((b) => b?.code !== APPS_UNCONFIRMED);
        // Only the app count is off (an app was added or deleted since the
        // check): stay here, show the new number and ask for it again. Nothing
        // was deleted, so the Google proof still holds.
        if (unconfirmed && others.length === 0) {
          setSummary((prev) => ({ ...prev, studioApps: Math.max(0, Number(unconfirmed.count) || 0) }));
          setAppsTyped("");
          setAppsError(true);
          setError(t("deleteAccount.errorStudioAppsUnconfirmed", { count: Number(unconfirmed.count) || 0 }));
          focusLater(appsRef);
          return;
        }
        showBlockers(others);
        return;
      }

      const baseKey = errorKeyFor(err, method);
      const key = looksAlreadyDeleted(err) ? "errorMaybeDeleted" : baseKey;
      // Remember how this attempt ended for the next one. Only an HTTP answer
      // teaches us anything; a Firebase error before the request does not.
      if (outcomeUnknown(err)) mayBeDeletedRef.current = true;
      else if (err?.response) mayBeDeletedRef.current = false;

      const clearPassword = baseKey === "errorWrongPassword" || baseKey === "errorReauthAgain";
      if (clearPassword) setPassword("");
      // The server revokes refresh tokens early in the deletion, so after any
      // failure the Google proof from before is dead: the next getIdToken(true)
      // would fail and the SDK would sign the person out mid-deletion. A new
      // popup mints fresh tokens that pass the server's revoked-token check.
      setGoogleConfirmed(false);
      setError(t(`deleteAccount.${key}`));

      // Back to whatever has to be done before trying again.
      if (method === "password" && clearPassword) focusLater(passwordRef);
      else if (method === "google") focusLater(googleButtonRef);
      else focusLater(deleteButtonRef);
      return;
    }

    // The account is gone on the server from here on, so nothing below may
    // turn into "we couldn't delete it". The backend already destroyed the
    // Express session, which is why this is NOT logOut(): that would POST
    // /logout for a session that no longer exists.
    clearPromptLabDrafts(uid);
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Sign-out after account deletion failed:", err);
    }
    setUser(null);
    // Same tick as setUser(null), so React renders both together and this
    // route's sign-in guard never gets to send the person to /login-page.
    // The state is what lets /account-deleted tell a real deletion from
    // someone typing the address, and only promise an email that was sent.
    navigate("/account-deleted", {
      replace: true,
      state: { deleted: true, emailSent: result?.emailSent === true },
    });
  };

  const blockerText = (blocker) => {
    const key = BLOCKER_KEYS[blocker?.code] || "blockerUnknown";
    return t(`deleteAccount.${key}`, {
      count: Number(blocker?.count) || 0,
      credits: formatCredits(blocker?.credits, i18n?.language),
    });
  };

  const errorBox = error && (
    <div className="cn-error-light" role="alert">{error}</div>
  );

  return (
    <>
      <hr className="cn-panel-rule" />

      <section className="cn-acct-danger" aria-labelledby="delete-account-title">
        <div className="cn-acct-head">
          <div>
            <h2 id="delete-account-title" className="cn-panel-title">{t("deleteAccount.title")}</h2>
            <p className="cn-panel-sub">{t("deleteAccount.intro")}</p>
          </div>
        </div>

        {step === "idle" && (
          <>
            {errorBox}
            <button
              ref={startRef}
              type="button"
              className="cn-btn-ghost cn-btn-dark cn-btn-danger-quiet"
              onClick={runCheck}
              aria-disabled={checking}
            >
              {checking ? t("deleteAccount.checking") : t("deleteAccount.start")}
            </button>
          </>
        )}

        {step === "blocked" && (
          <div className="cn-acct-danger-panel" role="group" aria-labelledby="delete-account-blocked-title">
            <h3
              id="delete-account-blocked-title"
              ref={blockedTitleRef}
              tabIndex={-1}
              className="cn-acct-danger-title"
            >
              {t("deleteAccount.blockedTitle")}
            </h3>
            <p className="cn-acct-note">{t("deleteAccount.blockedIntro")}</p>

            <ul className="cn-acct-danger-list">
              {blockers.map((blocker, i) => (
                <li key={`${blocker?.code}-${i}`}>
                  {blockerText(blocker)}
                  {blocker?.code === "active_subscription" && onGoToBilling && (
                    <span className="cn-acct-danger-next">
                      <button type="button" className="cn-link-button" onClick={onGoToBilling}>
                        {t("deleteAccount.goToBilling")}
                      </button>
                    </span>
                  )}
                  {(NEEDS_CONTACT.has(blocker?.code) || !BLOCKER_KEYS[blocker?.code]) && (
                    <span className="cn-acct-danger-next">
                      <Link to="/contact">{t("deleteAccount.contactCta")}</Link>{" "}
                      {t("deleteAccount.contactWithin")}
                    </span>
                  )}
                </li>
              ))}
            </ul>

            {errorBox}

            <div className="cn-acct-actions">
              <button
                ref={checkAgainRef}
                type="button"
                className="cn-btn-ghost cn-btn-dark"
                onClick={runCheck}
                aria-disabled={checking}
              >
                {checking ? t("deleteAccount.checking") : t("deleteAccount.checkAgain")}
              </button>
              <button type="button" className="cn-btn-ghost cn-btn-dark" onClick={cancel}>
                {t("deleteAccount.close")}
              </button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <div className="cn-acct-danger-panel" role="group" aria-labelledby="delete-account-confirm-title">
            <h3
              id="delete-account-confirm-title"
              ref={confirmTitleRef}
              tabIndex={-1}
              className="cn-acct-danger-title"
            >
              {t("deleteAccount.confirmTitle")}
            </h3>

            <p className="cn-acct-danger-label">{t("deleteAccount.deletesTitle")}</p>
            <ul className="cn-acct-danger-list">
              <li>{t("deleteAccount.deletesProfile")}</li>
              <li>{t("deleteAccount.deletesProgress")}</li>
              <li>{t("deleteAccount.deletesPromptLab")}</li>
              <li>{t("deleteAccount.deletesDesktop")}</li>
              {studioApps > 0 && <li>{t("deleteAccount.deletesStudioApps", { count: studioApps })}</li>}
              {contactProven && <li>{t("deleteAccount.deletesContact", { email: contactEmail })}</li>}
              <li>{t("deleteAccount.deletesCard")}</li>
            </ul>

            <p className="cn-acct-danger-label">{t("deleteAccount.keepsTitle")}</p>
            <ul className="cn-acct-danger-list">
              <li>{t("deleteAccount.keepsPayments")}</li>
              <li>{t("deleteAccount.keepsHosting")}</li>
              <li>{t("deleteAccount.keepsDesktop")}</li>
              {!contactProven && <li>{t("deleteAccount.keepsContact")}</li>}
            </ul>

            {forfeitsMessages && <p className="cn-acct-note">{t("deleteAccount.forfeitMessages")}</p>}

            <form onSubmit={submitDelete} className="cn-acct-form" noValidate>
              {/* Never pre-ticked: the tick is the person's own statement. */}
              <label className="cn-acct-check" htmlFor="delete-account-understand">
                <input
                  ref={checkboxRef}
                  id="delete-account-understand"
                  type="checkbox"
                  checked={understood}
                  onChange={(e) => setUnderstood(e.target.checked)}
                  disabled={working !== null}
                />
                <span>{t("deleteAccount.understand")}</span>
              </label>

              {studioApps > 0 && (
                <div className="cn-field">
                  <label className="cn-label-light" htmlFor="delete-account-studio-apps">
                    {t("deleteAccount.studioAppsConfirmLabel", { count: studioApps })}
                  </label>
                  {/* readOnly while working, like the password field. */}
                  <input
                    ref={appsRef}
                    id="delete-account-studio-apps"
                    className="cn-input-light"
                    name="studioAppsConfirmed"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="1"
                    autoComplete="off"
                    value={appsTyped}
                    onChange={(e) => {
                      setAppsTyped(e.target.value);
                      setAppsError(false);
                    }}
                    readOnly={working !== null}
                    aria-invalid={appsError || undefined}
                  />
                </div>
              )}

              {method === "password" && (
                <div className="cn-field cn-field-last">
                  <label className="cn-label-light" htmlFor="delete-account-password">
                    {t("deleteAccount.passwordLabel")}
                  </label>
                  {/* readOnly rather than disabled while working: pressing
                      Enter here submits, and a disabled field would drop the
                      focus it holds to <body>. */}
                  <input
                    ref={passwordRef}
                    id="delete-account-password"
                    className="cn-input-light"
                    name="currentPassword"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    readOnly={working !== null}
                  />
                </div>
              )}

              {method === "google" && (
                <div className="cn-field cn-field-last">
                  <p className="cn-acct-note">{t("deleteAccount.googleIntro")}</p>
                  {!googleConfirmed && (
                    <button
                      ref={googleButtonRef}
                      type="button"
                      className="cn-btn-ghost cn-btn-dark"
                      onClick={confirmWithGoogle}
                      aria-disabled={working !== null}
                    >
                      {working === "google" ? t("deleteAccount.googleConfirming") : t("deleteAccount.googleConfirm")}
                    </button>
                  )}
                  {/* Always mounted, only its text changes: many screen
                      readers ignore a live region that arrives already
                      holding its message. */}
                  <div
                    role="status"
                    aria-live="polite"
                    className={googleConfirmed ? "cn-success-light" : undefined}
                  >
                    {googleConfirmed ? t("deleteAccount.googleConfirmed") : ""}
                  </div>
                </div>
              )}

              {errorBox}

              <div className="cn-acct-actions">
                <button
                  ref={deleteButtonRef}
                  type="submit"
                  className="cn-btn-primary cn-btn-danger"
                  disabled={!ready}
                  aria-disabled={working !== null}
                  aria-describedby={!ready && working === null ? "delete-account-hint" : undefined}
                >
                  {working === "delete" ? t("deleteAccount.deleting") : t("deleteAccount.deleteButton")}
                </button>
                <button
                  type="button"
                  className="cn-btn-ghost cn-btn-dark"
                  onClick={cancel}
                  disabled={working !== null}
                >
                  {t("deleteAccount.cancel")}
                </button>
              </div>

              {/* Says why the button is off, so a disabled button is not a dead end. */}
              {!ready && working === null && (
                <p id="delete-account-hint" className="cn-acct-note cn-acct-danger-hint">
                  {t(studioApps > 0 ? "deleteAccount.deleteHintStudioApps" : "deleteAccount.deleteHint")}
                </p>
              )}
            </form>
          </div>
        )}
      </section>
    </>
  );
}
